import TTLCache from '@isaacs/ttlcache';
import _ from 'lodash';
import moment from 'moment';
import { handleStartHolterStudyProcessing } from '@/entities/study';
import { ANNOTATION_TYPE, Highpass, Lowpass } from '@/shared/ui';
import { KEY_CANCEL, TYPE_BEAT_ENUM, TYPE_EVENT_OTHER } from '@/shared/config';
import {
  convertEventNameFromType,
  convertToMomentJs,
  eventTypeConvert,
} from '@/shared/lib/aiUtils';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  CONFIG_ECG,
  addSineWaveToEcgData,
  convertBinaryToECGData,
  downloadSelectedEcgData,
} from '@/entities/ecg';
import { filterHourlySummaryRanges } from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import { isSameBeats } from '@/shared/lib';

export const LIMIT_BEATS_UPDATE = 5000;
const CACHE_ECG = new TTLCache({ max: 800, ttl: 900000 }); // 15 minutes

/**
 * Guards ECG sample arithmetic against NaN and infinities coming from raw device data. Returns the value when Number.isFinite accepts it, otherwise the supplied default.
 *
 * @param value - The candidate sample value.
 * @param defaultValue - Substitute used when the value is not finite, zero by default.
 * @returns The finite value, or the default.
 */
const ensureFiniteValue = (value: any, defaultValue = 0) =>
  Number.isFinite(value) ? value : defaultValue;

/**
 * Finds the median of an ECG sample series so it can serve as that channel's baseline. Drops non-finite samples, returns 0 when none remain, sorts a copy numerically and averages the two middle values for an even count.
 *
 * @param data - Sample values of one ECG channel.
 * @returns The median sample value, or 0 when no finite sample exists.
 */
export const getMedian = (data: any[]) => {
  const finiteValues = data.filter(Number.isFinite);
  if (finiteValues.length === 0) {
    return 0;
  }
  const ordered = finiteValues.slice();
  ordered.sort((a: number, b: number) => a - b);
  const middle = Math.floor((ordered.length - 1) / 2);
  if (ordered.length % 2) {
    return ordered[middle];
  }
  return (ordered[middle] + ordered[middle + 1]) / 2.0;
};

const beatTypeStringConvert = {
  60: 'V',
  70: 'S',
  80: 'Q',
  90: 'N',
  100: 'Q',
  1000: 'DEL', // Special case for delete beat in ecg viewer
};

/**
 * Translates a numeric HES beat status into the single-letter beat type shown on strips. Looks the status up in beatTypeStringConvert and falls back to the normal beat letter N.
 *
 * @param hesBeatStatus - Numeric HES beat status from the backend.
 * @returns The beat type letter for the status, or N.
 */
export const generateBeatTypeString = (hesBeatStatus: any) =>
  (beatTypeStringConvert as any)[hesBeatStatus] || 'N';

const hesBeatStatusConvert = {
  V: 60,
  S: 70,
  Q: 80,
  N: 90,
  DEL: 1000, // Special case for delete beat in ecg viewer
};

/**
 * Translates a single-letter beat type back into the numeric HES beat status the backend expects. Looks the letter up in hesBeatStatusConvert and falls back to 1.
 *
 * @param beatTypeString - Single-letter beat type such as N, S, V or Q.
 * @returns The numeric HES status for the letter, or 1.
 */
export const generateHesBeatStatus = (beatTypeString: any) =>
  (hesBeatStatusConvert as any)[beatTypeString] || 1;

/**
 * Zips the parallel beat arrays of a strip into per-beat objects for RR interval rendering. Pairs each beat sample index with its channel and heat-map cell, converting the numeric HES status into a letter stored both as type and originalType.
 *
 * @param beats - Beat sample indexes within the strip.
 * @param hesBeatStatus - Numeric HES status per beat, positionally aligned with beats.
 * @param beatChannels - Channel number per beat.
 * @param beatHeatMapIds - Heat-map cell id per beat, may be absent.
 * @returns One object per beat carrying id, type, channel, cellId and originalType.
 */
export const formatBeatStripRr = (
  beats: any[],
  hesBeatStatus: any[],
  beatChannels: any[],
  beatHeatMapIds: any[],
) =>
  _.map(beats, (d, index) => ({
    id: d,
    type: generateBeatTypeString(hesBeatStatus[index]),
    channel: beatChannels[index],
    cellId: beatHeatMapIds?.[index],
    originalType: generateBeatTypeString(hesBeatStatus[index]),
  }));

/**
 * Keys a cached ECG download by the time window it covers. Joins the start and stop epochs with a hyphen.
 *
 * @param start - Epoch timestamp at which the window begins.
 * @param stop - Epoch timestamp at which the window ends.
 * @returns The hyphen-joined cache key for the window.
 */
const generateId = (start: any, stop: any) => `${start}-${stop}`;

/**
 * Fetches the ECG samples surrounding a beat and returns them as drawable channel data. Rounds the beat epoch onto the sampling grid, derives the window from momentObject, a custom offset or the plain offset, serves a cache hit by window id, otherwise resolves the hourly ranges, downloads the binary, converts it to channel data, pads it with a sine wave and caches the result.
 *
 * @param ecgDataMap - Study ECG metadata carrying channels, samplingFrequency and querySignature.
 * @param beat - The beat to center the window on, by start date or epoch id.
 * @param offset - Symmetric window half-width in milliseconds.
 * @param momentObject - Explicit startMoment and stopMoment overriding the offsets.
 * @param dataLength - Expected sample count, used when padding the downloaded data.
 * @param config - Options carrying customOffset as a leading and trailing pair.
 * @returns A promise resolving to the sine-wave-padded ECG channel data for the window.
 */
export const handleDownloadData = async ({
  ecgDataMap,
  beat,
  offset,
  momentObject = {} as any,
  dataLength,
  config,
}: {
  ecgDataMap: any;
  beat?: any;
  offset?: any;
  momentObject?: any;
  dataLength?: any;
  config?: any;
}) => {
  // *: dataLength = samplingRate * minutes
  const { customOffset } = config || {};

  let epoch = beat?.start ? new Date(beat.start).getTime() : beat?.id;

  // * Round epoch to get rounded bytes
  const dividedNumber = 1000 / ecgDataMap.samplingFrequency;
  epoch = Math.floor(epoch / dividedNumber) * dividedNumber;

  let startMoment;
  let stopMoment;
  if (!_.isEmpty(momentObject)) {
    // *: Draw selected event
    startMoment = momentObject.startMoment;
    stopMoment = momentObject.stopMoment;
  } else if (!_.isEmpty(customOffset)) {
    startMoment = moment(epoch - customOffset[0]);
    stopMoment = moment(epoch + customOffset[1]);
  } else if (offset) {
    startMoment = moment(epoch - offset);
    stopMoment = moment(epoch + offset);
  }
  const idAPI = generateId(startMoment.valueOf(), stopMoment.valueOf());
  if (CACHE_ECG.has(idAPI)) {
    return CACHE_ECG.get(idAPI);
  }
  const { channels, samplingFrequency } = ecgDataMap;
  // filterHourlySummaryRanges's start/stop (handler.ts, Task 7) are typed number, but every
  // call site here passes a Moment - pre-existing.
  const ranges = filterHourlySummaryRanges({
    summaries: ecgDataMap,
    start: startMoment,
    stop: stopMoment,
    channels,
    samplingFrequency,
    willAddDataRanges: true,
  } as any);
  try {
    const downloadedSelectedEcgData = await downloadSelectedEcgData(
      ranges,
      ecgDataMap.querySignature,
      { cancelKey: KEY_CANCEL.DOWLOAD_ECG },
    );
    if (downloadedSelectedEcgData?.length) {
      const selectedEcgData = convertBinaryToECGData(
        channels.length,
        downloadedSelectedEcgData as any,
      );
      const addedSineWaveSelectedEcgData = addSineWaveToEcgData(
        selectedEcgData,
        ranges,
        startMoment,
        stopMoment,
        ecgDataMap,
        dataLength,
      );
      CACHE_ECG.set(idAPI, addedSineWaveSelectedEcgData);
      return addedSineWaveSelectedEcgData;
    }
    throw new Error('Error');
  } catch (error) {
    console.error('Error download data: ', error);
    throw error;
  }
};

let canClearCache = true;

/**
 * Flushes the in-memory ECG download cache while throttling repeated calls. Returns immediately when a clear already happened, otherwise empties CACHE_ECG and re-enables clearing 500ms later.
 *
 * @returns Nothing.
 */
export const clearCacheAPI = () => {
  if (!canClearCache) {
    return;
  }
  canClearCache = false;
  CACHE_ECG.clear();
  setTimeout(() => {
    canClearCache = true;
  }, 500);
};

// export const updateListBeatReview = (beats, stripSelect, isReviewed) => {
//   const data = _.cloneDeep(beats);
//   // Case toggle review
//   if (stripSelect.isReviewed === isReviewed) {
//     _.remove(data, (x) => {
//       if (x.id === stripSelect?.id) {
//         // If dont have type beat => have not change to Artifact or Delete => remove from changes array
//         if (_.isNil(x.type)) {
//           return true;
//         }
//         // Assign review state to keep changed type
//         _.assign(x, { isReviewed });
//         return false;
//       }
//       return false;
//     });
//     return data;
//   }
//   const ele = _.find(data, x => x.id === stripSelect?.id);
//   // Event has changed
//   if (ele) {
//     if (isReviewed === ele.oldReviewed) {
//       _.remove(data, (x) => {
//         if (x.id === stripSelect?.id) {
//           // If dont have type beat => have not change to Artifact or Delete => remove from changes array
//           if (_.isNil(x.type)) {
//             return true;
//           }
//           // Assign review state to keep changed type beat
//           _.assign(x, { isReviewed });
//           return false;
//         }
//         return false;
//       });
//       return data;
//     }
//     // For multiple select and mark review
//     if (isReviewed === ele.isReviewed) {
//       return data;
//     }
//     _.assign(ele, { isReviewed, oldReviewed: ele.oldReviewed || ele.isReviewed });
//   } else {
//     data.push({
//       ...stripSelect,
//       ...{
//         isReviewed,
//         oldReviewed: stripSelect?.isReviewed,
//         oldType: stripSelect.type,
//       },
//     });
//   }
//   return data;
// };

export const updateStripReview = ({
  stripChanges = [] as any[],
  strip,
  isReviewed,
}: {
  stripChanges?: any[];
  strip: any;
  isReviewed: any;
}) => {
  const cloneStripChanges = _.cloneDeep(stripChanges);
  let indexRemove;
  let hasBeenChanged;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    if (cloneStripChanges[i].id === strip?.id) {
      hasBeenChanged = true;
      if (strip.isReviewed === isReviewed) {
        if (_.isNil(strip.type)) {
          indexRemove = i;
          break;
        }
      }
      const stripChangeEl = cloneStripChanges[i];
      if (stripChangeEl.oldReviewed === isReviewed) {
        if (_.isNil(stripChangeEl.type)) {
          indexRemove = i;
          break;
        }
      }
      if (stripChangeEl.isReviewed === isReviewed) {
        return cloneStripChanges;
      }
      stripChangeEl.isReviewed = isReviewed;
      stripChangeEl.oldReviewed = stripChangeEl.oldReviewed || stripChangeEl.isReviewed;
    }
  }
  if (!_.isNil(indexRemove)) {
    cloneStripChanges.splice(indexRemove, 1);
  }
  if (!hasBeenChanged && strip.isReviewed !== isReviewed) {
    cloneStripChanges.push({
      ...strip,
      ...{
        isReviewed,
        oldReviewed: strip.isReviewed,
        oldType: strip.type,
      },
    });
  }
  return cloneStripChanges;
};

export const updateMultipleStripReview = ({
  stripChanges = [] as any[],
  strips = [] as any[],
  isReviewed,
}: {
  stripChanges?: any[];
  strips?: any[];
  isReviewed: any;
}) => {
  const cloneStripChanges = _.cloneDeep(stripChanges);
  const indexRemoves: any[] = [];
  const idHasBeenChangedStrips: any[] = [];
  let breakFirstLoop = false;
  let findCounter = 0;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    for (let j = 0; j < strips.length; j += 1) {
      if (cloneStripChanges[i].id === strips[j].id) {
        findCounter += 1;
        //* If done search for all strips then early break the loop
        if (findCounter === strips.length) {
          breakFirstLoop = true;
        }
        idHasBeenChangedStrips.push(strips[j].id);
        if (strips[j].isReviewed === isReviewed) {
          if (_.isNil(strips[j].type)) {
            indexRemoves.push(i);
            break;
          }
        }
        const stripChangeEl = cloneStripChanges[i];
        if (stripChangeEl.oldReviewed === isReviewed) {
          if (_.isNil(stripChangeEl.type)) {
            indexRemoves.push(i);
            break;
          }
        }
        if (stripChangeEl.isReviewed === isReviewed) {
          break;
        }
        stripChangeEl.isReviewed = isReviewed;
        stripChangeEl.oldReviewed = stripChangeEl.oldReviewed || stripChangeEl.isReviewed;
      }
    }
    if (breakFirstLoop) {
      break;
    }
  }
  // Splice in reverse order to prevent mess up index
  for (let i = indexRemoves.length - 1; i >= 0; i -= 1) {
    cloneStripChanges.splice(indexRemoves[i], 1);
  }
  _.forEach(strips, (strip) => {
    if (!idHasBeenChangedStrips.includes(strip.id) && strip.isReviewed !== isReviewed) {
      cloneStripChanges.push({
        ...strip,
        ...{
          isReviewed,
          oldReviewed: strip.isReviewed,
          oldType: strip.type,
        },
      });
    }
  });
  return cloneStripChanges;
};

export const updateStripType = ({
  stripChanges = [] as any[],
  strip,
  type,
  isEvent = true,
}: {
  stripChanges?: any[];
  strip: any;
  type: any;
  isEvent?: boolean;
}) => {
  // case beat current type === update type => remove if in beat change
  // case beat has changed and update to new type === original type => remove if in beat change
  // case beat has changed and update to new type === current type => unchange
  // case beat has changed and update to new type !== original type
  const cloneStripChanges = _.cloneDeep(stripChanges);
  let indexRemove;
  let hasBeenChanged;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    const compareField = isEvent ? 'idEvent' : 'id';
    if (cloneStripChanges[i]?.[compareField] === strip?.[compareField]) {
      hasBeenChanged = true;
      const stripChangeEl = cloneStripChanges[i];
      if (strip.type === type && !isEvent) {
        indexRemove = i;
        break;
      }
      if (stripChangeEl.oldType === type) {
        indexRemove = i;
        break;
      }
      if (stripChangeEl.type === type) {
        return cloneStripChanges;
      }
      stripChangeEl.type = type;
      stripChangeEl.oldType = stripChangeEl.oldType || stripChangeEl.type;
    }
  }
  if (!_.isNil(indexRemove)) {
    cloneStripChanges.splice(indexRemove, 1);
  }
  if (!hasBeenChanged && strip.type !== type) {
    cloneStripChanges.push({
      ...strip,
      ...{
        type,
        oldType: strip.type,
        isReviewed: strip.isReviewed,
        oldReviewed: strip.isReviewed,
      },
    });
  }
  return cloneStripChanges;
};

export const updateMultipleStripType = ({
  stripChanges = [] as any[],
  strips = [] as any[],
  type,
  isEvent = true,
}: {
  stripChanges?: any[];
  strips?: any[];
  type: any;
  isEvent?: boolean;
}) => {
  const cloneStripChanges = _.cloneDeep(stripChanges);
  const indexRemoves: any[] = [];
  const idHasBeenChangedStrips: any[] = [];
  let breakFirstLoop = false;
  let findCounter = 0;
  const isResettingChanges = !type;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    for (let j = 0; j < strips.length; j += 1) {
      const compareField = isEvent ? 'idEvent' : 'id';
      if (cloneStripChanges[i]?.[compareField] === strips[j]?.[compareField]) {
        findCounter += 1;
        //* If done search for all strips then early break the loop
        if (findCounter === strips.length) {
          breakFirstLoop = true;
        }
        idHasBeenChangedStrips.push(strips[j].id);
        if (isResettingChanges || (strips[j].type === type && !isEvent)) {
          indexRemoves.push(i);
          break;
        }
        const stripChangeEl = cloneStripChanges[i];
        if (isResettingChanges || stripChangeEl.oldType === type) {
          indexRemoves.push(i);
          break;
        }
        if (stripChangeEl.type === type) {
          break;
        }
        stripChangeEl.type = type;
        stripChangeEl.oldType = stripChangeEl.oldType || stripChangeEl.type;
      }
    }
    if (breakFirstLoop) {
      break;
    }
  }

  //* Splice in reverse order to prevent mess up index
  for (let i = indexRemoves.length - 1; i >= 0; i -= 1) {
    cloneStripChanges.splice(indexRemoves[i], 1);
  }

  _.forEach(strips, (strip) => {
    if (!idHasBeenChangedStrips.includes(strip.id) && type && strip.type !== type) {
      cloneStripChanges.push({
        ...strip,
        ...{
          type,
          oldType: strip.type,
          isReviewed: strip.isReviewed,
          oldReviewed: strip.isReviewed,
        },
      });
    }
  });

  return cloneStripChanges;
};

// export const updateListBeatChange = (beats, stripSelect, type) => {
//   const data = _.cloneDeep(beats);
//   // case beat orginal type === update type => remove if in beat change
//   if (stripSelect.type === type) {
//     _.remove(data, x => x.id === stripSelect?.id);
//     return data;
//   }
//   const ele = _.find(data, x => x.id === stripSelect?.id);
//   if (ele) {
//     if (type === ele.oldType) {
//       // case beat has changed and update to new type === original type
//       _.remove(data, x => x.id === stripSelect?.id);
//       return data;
//     }
//     if (type === ele.type) {
//       // case beat has changed and update to new type === current type
//       return data;
//     }
//     // case beat has changed and update to new type !== original type
//     _.assign(ele, { type, oldType: ele.oldType || ele.type });
//   } else {
//     data.push({
//       ...stripSelect,
//       ...{
//         type,
//         oldType: stripSelect.type,
//         isReviewed: stripSelect?.isReviewed,
//         oldReviewed: stripSelect?.isReviewed,
//       },
//     });
//   }
//   return data;
// };

export const handleAddEventOthersV2 = ({
  events = [] as any[],
  draw,
  allIndexStripSvg,
  technicianComments,
  captureTitle,
  startTime,
  endTime,
  miniteInfo,
}: {
  events?: any[];
  draw: any;
  allIndexStripSvg: any;
  technicianComments: any;
  captureTitle: any;
  startTime: any;
  endTime: any;
  miniteInfo: any;
}) => {
  try {
    const miniteData = _.cloneDeep(miniteInfo);
    const existSameTime = _.find(miniteData, (x) => x.start === startTime && x.stop === endTime);
    const data = _.cloneDeep(events);
    const ele = _.find(data, (x) => x.start === startTime && x.stop === endTime);
    if (ele) {
      _.assign(ele, {
        technicianComments,
        title: captureTitle,
        stripImgs: allIndexStripSvg,
        draw,
        isUpdatedOther: true,
        captures: [
          {
            ...(ele?.captures?.[0] || {}),
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            captureId: new Date().getTime(),
            isNewCapture: true,
            action: 'update',
          },
        ],
      });
    } else if (existSameTime) {
      _.assign(existSameTime, {
        technicianComments,
        title: captureTitle,
        stripImgs: allIndexStripSvg,
        draw,
        isUpdatedOther: true,
        captures: [
          {
            ...(existSameTime?.captures?.[0] || {}),
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            captureId: new Date().getTime(),
            isUpdateOtherImage: true,
            action: 'update',
          },
        ],
      });
      data.push(existSameTime);
    } else {
      data.push({
        start: startTime,
        stop: endTime,
        type: TYPE_EVENT_OTHER,
        technicianComments,
        title: captureTitle,
        isUpdatedOther: true,
        stripImgs: allIndexStripSvg,
        draw,
        id: new Date().getTime(),
        captures: [
          {
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            captureId: new Date().getTime(),
            isNewCapture: true,
            action: 'update',
          },
        ],
      });
    }
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * Records edited strip annotations and images against an ECG event in the pending-changes list. Overwrites the capture whose sample-mark start and end times match the incoming annotation, otherwise appends a new capture, then rolls the combined draw data, images and technician comments onto the event or pushes a fresh pending entry keeping the original as originEvent.
 *
 * @param events - The pending ECG event changes.
 * @param stripSelect - The strip whose event is being annotated.
 * @param draw - Annotation shapes for the capture, including the sample mark.
 * @param allIndexStripSvg - Rendered strip images to store on the capture.
 * @param technicianComments - Technician comments to store on the capture.
 * @returns The pending event list with the annotation and image changes applied.
 */
export const updateListImagesChangeV2 = (
  events: any[],
  stripSelect: any,
  draw: any,
  allIndexStripSvg: any,
  technicianComments: any,
) => {
  try {
    const data = _.cloneDeep(events);
    const ele = _.find(data, (x) => x.idEvent === stripSelect?.idEvent);
    const findDraw = _.find(draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
    if (ele) {
      const captures = _.cloneDeep(ele.captures || []);
      let isReplace = false;
      const drawData = [];
      _.forEach(captures, (capture) => {
        const sampleMark = _.find(capture.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
        if (
          sampleMark?.startTime === findDraw?.startTime &&
          sampleMark?.endTime === findDraw?.endTime
        ) {
          _.assign(capture, {
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            action: 'update',
          });
          isReplace = true;
          drawData.push(...draw);
        } else {
          drawData.push(...capture.draw);
        }
      });
      if (!isReplace) {
        captures.push({
          draw,
          stripImgs: allIndexStripSvg,
          technicianComments,
          captureId: new Date().getTime(),
          isNewCapture: true,
          action: 'update',
        });
        drawData.push(...draw);
      }
      _.assign(ele, {
        draw: drawData,
        stripImgs: allIndexStripSvg,
        technicianComments,
        isUpdateImage: true,
        captures,
      });
    } else {
      const captures = _.cloneDeep(stripSelect.captures || []);
      const drawData = [];
      let isReplace = false;
      _.forEach(captures, (capture) => {
        const sampleMark = _.find(capture.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
        if (
          sampleMark?.startTime === findDraw?.startTime &&
          sampleMark?.endTime === findDraw?.endTime
        ) {
          _.assign(capture, {
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            action: 'update',
          });
          isReplace = true;
          drawData.push(...draw);
        } else {
          drawData.push(...capture.draw);
        }
      });

      if (!isReplace) {
        captures.push({
          draw,
          stripImgs: allIndexStripSvg,
          technicianComments,
          captureId: new Date().getTime(),
          isNewCapture: true,
          action: 'update',
        });
        drawData.push(...draw);
      }

      data.push({
        ...stripSelect,
        originEvent: stripSelect,
        draw: drawData,
        stripImgs: allIndexStripSvg,
        technicianComments,
        isUpdateImage: true,
        captures,
      });
    }

    return data;
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * Kicks off backend Holter processing for a study from the HR summary tab. Wraps the study and profile ids into the mutation input and delegates to handleStartHolterStudyProcessing, logging the error and raising an error toast when the call fails.
 *
 * @param studyId - Identifier of the study to process.
 * @param profileId - Identifier of the report profile driving the processing.
 * @returns A promise resolving to the processing result, or to undefined when the call failed.
 */
export const callStartHolterStudyProcessing = async (studyId: any, profileId: any) => {
  try {
    const input = {
      studyId,
      profileId,
    };
    const result = await handleStartHolterStudyProcessing(input);
    return result;
  } catch (error) {
    logError(error.message, error);
    toastrError(error.message, 'Error');
  }
};

/**
 * Folds a strip's beat marker edits into the study-wide pending beat change list. Clones the list and, per edit time, appends a local beat, reassigns type and oldType, or queues the entry for removal when the edit restores the original server type or deletes a purely local beat, then drops every queued id.
 *
 * @param updatedBeats - Add, Remove and Edit beat marker entries from the strip.
 * @param beatChanges - The study-wide pending beat change list.
 * @returns The pending beat change list after the edits are folded in.
 */
export const handleUpdateBeatChanges = (updatedBeats: any[], beatChanges: any[]) => {
  if (updatedBeats.length === 0) {
    return beatChanges;
  }
  const clonedBeatChanges = _.cloneDeep(beatChanges);
  const idRemoves: any[] = [];
  _.forEach(updatedBeats, (b: any) => {
    const { actionType, time, type, oldType, cellId } = b;
    /*
      Server beat change
      {
        id: 1648761703332
        oldType: "N"
        type: "Q"
      }
      Local beat change
      {
        id: 1648761703332
        type: "Q"
        isLocalBeat: true,
      }
    */
    if (actionType === 'Add') {
      // N/S/V
      const eleAdd = clonedBeatChanges.find((x: any) => x.id === time);
      if (eleAdd) {
        if (eleAdd.oldType === type) {
          // case: change type server beat (N,S,V,Q,Del) => beatChanges have beat => add beat same original type & same position (time) => remove
          idRemoves.push(eleAdd.id);
        } else {
          // case change type server || local beat => beatChanges have beat => add beat different original type & same position (time) => assign type
          _.assign(eleAdd, { type, oldType });
        }
      } else {
        // case first add new local beat
        clonedBeatChanges.push({
          id: time,
          type,
          isLocalBeat: true,
          cellId,
        });
      }
    }
    if (actionType === 'Remove') {
      const eleRemove = clonedBeatChanges.find((x: any) => x.id === time);
      if (eleRemove) {
        if (eleRemove.isLocalBeat) {
          // case delete local beat => remove
          idRemoves.push(eleRemove.id);
        } else {
          // case delete server beat => assign deleted type
          _.assign(eleRemove, { type, oldType });
        }
      } else {
        // case first deleted server beat
        clonedBeatChanges.push({
          id: time,
          type,
          oldType,
          cellId,
        });
      }
    }
    if (actionType === 'Edit') {
      const eleEdit = clonedBeatChanges.find((x: any) => x.id === time);
      if (eleEdit) {
        if (eleEdit.isLocalBeat) {
          // case edit local beat
          _.assign(eleEdit, { type, oldType });
        } else if (eleEdit.oldType === type) {
          // case edit server beat same original type
          idRemoves.push(eleEdit.id);
        } else {
          // case edit server beat different original type
          _.assign(eleEdit, { type, oldType });
        }
      } else {
        // case first edit server beat
        clonedBeatChanges.push({
          id: time,
          type,
          oldType,
          cellId,
        });
      }
    }
  });
  if (idRemoves.length) {
    _.remove(clonedBeatChanges, (x) => idRemoves.includes(x.id));
  }
  return clonedBeatChanges;
};

/**
 * Converts a beat's absolute epoch timestamp into its sample index inside an ECG strip. Divides the millisecond distance from the strip start by 1000 and multiplies by the configured ECG sampling frequency, rounding to the nearest sample.
 *
 * @param beatEpoch - Absolute epoch timestamp of the beat in milliseconds.
 * @param startEpoch - Epoch timestamp at which the strip begins.
 * @returns The rounded sample index of the beat within the strip.
 */
export const calculateBeatPosition = (beatEpoch: number, startEpoch: number) => {
  const beatDiffSeconds = (beatEpoch - startEpoch) / 1000;
  const beatPosition = Math.round(beatDiffSeconds * CONFIG_ECG.samplingFrequency);
  return beatPosition;
};

/**
 * Replays pending beat edits onto the beat and status arrays of one ECG strip. Keeps only changes falling inside the strip window, converts each change epoch to a sample position, and for N, S, V or Q either overwrites a beat within one sample or splices a new one in sorted order, while any other type removes the matching beat and its status.
 *
 * @param beats - Beat sample indexes of the strip.
 * @param hesBeatStatus - Numeric HES status per beat, positionally aligned with beats.
 * @param beatChanges - Pending beat edits keyed by epoch id.
 * @param startEpoch - Epoch timestamp at which the strip begins.
 * @param stopEpoch - Epoch timestamp at which the strip ends.
 * @returns An object carrying updatedBeats and updatedHesBeatStatus after the edits are applied.
 */
export const generateUpdatedBeats = (
  beats: any[] = [],
  hesBeatStatus: any[] = [],
  beatChanges: any[] = [],
  startEpoch: any = '',
  stopEpoch: any = '',
) => {
  const updatedBeats = [...beats];
  const updatedHesBeatStatus = [...hesBeatStatus];
  /**
   * Narrows the pending beat edits to those falling inside the current strip window. Guards against a missing list and keeps entries whose epoch id sits between startEpoch and stopEpoch.
   *
   * @returns The beat changes inside the strip window, or the falsy input when none was supplied.
   */
  const filterBeatChanges =
    beatChanges && beatChanges.filter((x) => x.id >= startEpoch && x.id <= stopEpoch);
  _.forEach(filterBeatChanges, (item) => {
    const beatPosition = calculateBeatPosition(item.id, startEpoch);
    const compareBeatPositions = [beatPosition - 1, beatPosition, beatPosition + 1];
    if (_.includes(['N', 'S', 'V', 'Q'], item.type)) {
      const beatTypeNumber = generateHesBeatStatus(item.type);
      const editIndex = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      if (editIndex > -1) {
        updatedBeats[editIndex] = beatPosition;
        updatedHesBeatStatus[editIndex] = beatTypeNumber;
      } else {
        const addIndex = updatedBeats.findIndex((x) => x > beatPosition);
        if (addIndex > -1) {
          updatedBeats.splice(addIndex, 0, beatPosition);
          updatedHesBeatStatus.splice(addIndex, 0, beatTypeNumber);
        } else {
          updatedBeats.push(beatPosition);
          updatedHesBeatStatus.push(beatTypeNumber);
        }
      }
    } else {
      const index = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      updatedBeats.splice(index, 1);
      updatedHesBeatStatus.splice(index, 1);
    }
  });
  return {
    updatedBeats,
    updatedHesBeatStatus,
  };
};

export const generateUpdatedBeatsV2 = ({
  beats = [] as any[],
  hesBeatStatus = [] as any[],
  beatChanges = [] as any[],
  startEpoch,
  stopEpoch,
  beatHeatMapIds,
  applyBulkBeatsChanges,
  resetAppliedBulkBeatsChanges,
}: {
  beats?: any[];
  hesBeatStatus?: any[];
  beatChanges?: any[];
  startEpoch: any;
  stopEpoch: any;
  beatHeatMapIds?: any[];
  applyBulkBeatsChanges?: any;
  resetAppliedBulkBeatsChanges?: any;
}) => {
  const updatedBeats = [...beats];
  const updatedHesBeatStatus = [...hesBeatStatus];
  if (!_.isEmpty(applyBulkBeatsChanges)) {
    const resetBeatIndex: any[] = [];
    if (!_.isEmpty(resetAppliedBulkBeatsChanges)) {
      const filterResetAppliedBulkBeatsChanges = resetAppliedBulkBeatsChanges.filter(
        (x: any) => x.id >= startEpoch && x.id <= stopEpoch,
      );
      _.forEach(filterResetAppliedBulkBeatsChanges, (x: any) => {
        const position = calculateBeatPosition(x.id, startEpoch);
        const compareBeatPositions = [position - 1, position, position + 1];
        const resetIndex = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
        resetBeatIndex.push(resetIndex);
      });
    }
    _.forEach(beatHeatMapIds, (cellId: any, index: number) => {
      const changeCell = _.find(applyBulkBeatsChanges, (x: any) => x.cellId === cellId);
      if (changeCell && !_.includes(resetBeatIndex, index)) {
        if (_.includes(['N', 'S', 'V', 'Q'], changeCell.type)) {
          updatedHesBeatStatus[index] = generateHesBeatStatus(changeCell.type);
        } else {
          const beat = beats[index];
          const beatIndex = updatedBeats.findIndex((x) => x === beat);
          updatedBeats.splice(beatIndex, 1);
          updatedHesBeatStatus.splice(beatIndex, 1);
        }
      }
    });
  }

  /**
   * Narrows the pending beat edits to those falling inside the current strip window. Guards against a missing list and keeps entries whose epoch id sits between startEpoch and stopEpoch.
   *
   * @returns The beat changes inside the strip window, or the falsy input when none was supplied.
   */
  const filterBeatChanges =
    beatChanges && beatChanges.filter((x) => x.id >= startEpoch && x.id <= stopEpoch);
  _.forEach(filterBeatChanges, (item) => {
    const beatPosition = calculateBeatPosition(item.id, startEpoch);
    const compareBeatPositions = [beatPosition - 1, beatPosition, beatPosition + 1];
    if (_.includes(['N', 'S', 'V', 'Q'], item.type)) {
      const beatTypeNumber = generateHesBeatStatus(item.type);
      const editIndex = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      if (editIndex > -1) {
        updatedBeats[editIndex] = beatPosition;
        updatedHesBeatStatus[editIndex] = beatTypeNumber;
      } else {
        const addIndex = updatedBeats.findIndex((x) => x > beatPosition);
        if (addIndex > -1) {
          updatedBeats.splice(addIndex, 0, beatPosition);
          updatedHesBeatStatus.splice(addIndex, 0, beatTypeNumber);
        } else {
          updatedBeats.push(beatPosition);
          updatedHesBeatStatus.push(beatTypeNumber);
        }
      }
    } else {
      // *: Delete
      const index = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      updatedBeats.splice(index, 1);
      updatedHesBeatStatus.splice(index, 1);
    }
  });
  return {
    updatedBeats,
    updatedHesBeatStatus,
  };
};

/**
 * Diffs the current beat markers of a strip against the previous ones to produce the reviewer's edit list. Beats only in current become Add entries with the time floored onto the sampling-rate grid, beats only in previous become Remove entries typed as deleted with their original letter, and shared beats whose HES status changed become Edit entries.
 *
 * @param currentBeatMarkers - Beat sample positions after the edit.
 * @param prevBeatMarkers - Beat sample positions before the edit.
 * @param currentHesBeatStatus - HES status per current beat marker.
 * @param prevHesBeatStatus - HES status per previous beat marker.
 * @param originalHesBeatStatus - HES status per beat as first loaded, used for oldType.
 * @param startEpoch - Epoch timestamp at which the strip begins.
 * @param samplingFrequency - Sampling frequency used to snap Add times onto a grid.
 * @returns The Add, Remove and Edit beat marker entries describing the diff.
 */
export const getUpdatedBeatMarker = (
  currentBeatMarkers: any[],
  prevBeatMarkers: any[],
  currentHesBeatStatus: any[],
  prevHesBeatStatus: any[],
  originalHesBeatStatus: any[],
  startEpoch: number,
  samplingFrequency: number,
) => {
  const updatedBeatMarkers: any[] = [];
  // *: Has at current but not previous
  const beatMarkersAdded = _.difference(currentBeatMarkers, prevBeatMarkers);
  const dividedNumber = samplingFrequency / 250;
  _.forEach(beatMarkersAdded, (item: any) => {
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    const epoch = startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000;
    updatedBeatMarkers.push({
      time: Math.floor(epoch / dividedNumber) * dividedNumber,
      type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
      actionType: 'Add',
    });
  });

  // *: Has at previous but not current
  const beatMarkersRemoved = _.difference(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersRemoved, (item: any) => {
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    updatedBeatMarkers.push({
      time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      type: TYPE_BEAT_ENUM.DELETED,
      oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
      actionType: 'Remove',
    });
  });

  // *: Has both
  const beatMarkersIntersection = _.intersection(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersIntersection, (item: any) => {
    // *: Compare hesBeatStatus in currentHesBeatStatus (with currentIndex) to originalHesBeatStatus (with originalIndex)
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    if (!isSameBeats(currentHesBeatStatus[currentBeatIndex], prevHesBeatStatus[prevBeatIndex])) {
      updatedBeatMarkers.push({
        time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
        type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
        oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
        actionType: 'Edit',
      });
    }
  });
  return updatedBeatMarkers;
};

/**
 * Diffs current against previous beat markers and tags every edit with its heat-map cell. Splits the beats the same three ways, Add for new ones, Remove typed as deleted, Edit when the HES status differs, but leaves times unsnapped and additionally stamps Remove and Edit entries with id, the cellId looked up through originalBeatMarker, and the original beat letter.
 *
 * @param currentBeatMarkers - Beat sample positions after the edit.
 * @param prevBeatMarkers - Beat sample positions before the edit.
 * @param currentHesBeatStatus - HES status per current beat marker.
 * @param prevHesBeatStatus - HES status per previous beat marker.
 * @param originalHesBeatStatus - HES status per beat as first loaded, used for oldType.
 * @param startEpoch - Epoch timestamp at which the strip begins.
 * @param beatHeatMapIds - Heat-map cell id per originally loaded beat.
 * @param originalBeatMarker - Beat sample positions as first loaded, used to index the heat-map ids.
 * @returns The Add, Remove and Edit beat marker entries, carrying heat-map cell ids.
 */
export const getUpdatedBeatMarkerV2 = (
  currentBeatMarkers: any[],
  prevBeatMarkers: any[],
  currentHesBeatStatus: any[],
  prevHesBeatStatus: any[],
  originalHesBeatStatus: any[],
  startEpoch: number,
  beatHeatMapIds: any[] = [],
  originalBeatMarker: any[],
) => {
  const updatedBeatMarkers: any[] = [];
  // *: Has at current but not previous
  const beatMarkersAdded = _.difference(currentBeatMarkers, prevBeatMarkers);
  _.forEach(beatMarkersAdded, (item: any) => {
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    updatedBeatMarkers.push({
      time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
      actionType: 'Add',
    });
  });

  // *: Has at previous but not current
  const beatMarkersRemoved = _.difference(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersRemoved, (item: any) => {
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    const originalBeatIndex = _.findIndex(originalBeatMarker, (x) => x === item);
    updatedBeatMarkers.push({
      time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      id: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      type: TYPE_BEAT_ENUM.DELETED,
      oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
      actionType: 'Remove',
      cellId: beatHeatMapIds[originalBeatIndex],
    });
  });

  // *: Has both
  const beatMarkersIntersection = _.intersection(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersIntersection, (item: any) => {
    // *: Compare hesBeatStatus in currentHesBeatStatus (with currentIndex) to originalHesBeatStatus (with originalIndex)
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    const originalBeatIndex = _.findIndex(originalBeatMarker, (x) => x === item);
    if (!isSameBeats(currentHesBeatStatus[currentBeatIndex], prevHesBeatStatus[prevBeatIndex])) {
      updatedBeatMarkers.push({
        time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
        type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
        oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
        actionType: 'Edit',
        cellId: beatHeatMapIds[originalBeatIndex],
        originalType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
        id: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      });
    }
  });

  return updatedBeatMarkers;
};

/**
 * Picks the dominant ECG channel across a set of beats. Tallies the one-based channel numbers into a three-slot frequency array and returns the one-based index of the highest count.
 *
 * @param beatChannels - One-based channel number per beat.
 * @returns The one-based channel number occurring most often.
 */
export const countBeatChannels = (beatChannels: any[] = []) => {
  const channelFreq = beatChannels.reduce(
    (acc, curr) => {
      acc[curr - 1] += 1;
      return acc;
    },
    [0, 0, 0],
  );
  return _.indexOf(channelFreq, _.max(channelFreq)) + 1;
};

/**
 * Drops rows already flagged as being persisted from a change list. Counts the flagged rows while filtering and returns the original array untouched when none were found.
 *
 * @param data - Change rows possibly carrying an isSaving flag.
 * @returns The list without the isSaving rows, or the original array when nothing was flagged.
 */
export const removeSavingData = (data: any[]) => {
  let count = 0;
  const filterResult = data.filter((x) => {
    if (x.isSaving) {
      count += 1;
      return false;
    }
    return true;
  });
  if (count === 0) {
    return data;
  }
  return filterResult;
};

// convert hr from 250 to 300 (25 < hr < 300)
/**
 * Rejects heart rates outside the plausible band the HR summary editor accepts. Treats anything below 25 or above 300 bpm as out of range.
 *
 * @param hr - Heart rate in beats per minute.
 * @returns True when the heart rate falls outside 25 to 300 bpm.
 */
export const isOutOfRangeHr = (hr: number) => hr < 25 || hr > 300;

//* Preview strip
/**
 * Projects millivolt ECG channel samples into canvas pixel coordinates for waveform drawing. Spreads the channels evenly over the available height, then maps every sample to a floored x from indexToPix and a y taken from the channel baseline minus the value scaled by millivoltToPix.
 *
 * @param samples - Per-channel arrays of millivolt sample values.
 * @param config - Drawing configuration carrying height, millivoltToPix and indexToPix.
 * @returns Per-channel arrays of pixel points, each with x and y coordinates.
 */
export const calculatePixelSamples = (samples: any[], config: any) => {
  const { height, millivoltToPix, indexToPix } = config;
  const samplesLength = samples.length;
  const verticalChannelPlacement = samples.map(
    (chArr: any, chIdx: number) => (height / samplesLength) * (chIdx + 0.5),
  );
  const pixelSamples = samples.map((chArr: any, chIdx: number) =>
    chArr.map((u: any, i: number) => ({
      x: Math.floor(i * indexToPix),
      y: Math.floor(verticalChannelPlacement[chIdx] - u * millivoltToPix),
    })),
  );
  return pixelSamples;
};

/**
 * Turns raw ADC ECG counts into filtered millivolt channel samples ready for rendering. Substitutes adcZero for non-finite counts, rescales by adcGain, subtracts each channel's median baseline, then applies a highpass at LFF and a lowpass at HFF at the given sampling rate.
 *
 * @param ecgData - Per-channel arrays of raw ADC counts.
 * @param config - Signal configuration carrying adcZero, adcGain, LFF, HFF and samplingRate.
 * @returns Per-channel arrays of baseline-corrected, band-filtered samples.
 */
export const calculateSamples = (ecgData: any[], config: any) => {
  const { adcZero, adcGain, LFF, HFF, samplingRate } = config;
  const samples = ecgData.map((chData: any) =>
    chData.map((n: any) => (ensureFiniteValue(n, adcZero) - adcZero) / adcGain),
  );
  const B0 = samples.map(getMedian);
  const samplesLength = samples.length;
  samples.forEach((chArr: any, chIdx: number) => {
    const chArrLength = chArr.length;
    for (let j = 0; j < chArrLength; j += 1) {
      chArr[j] -= B0[chIdx];
    }
  });
  // *: Filter by 2Hz highpass and 20Hz lowpass
  for (let i = 0; i < samplesLength; i += 1) {
    samples[i] = Highpass(samples[i], LFF, samplingRate);
    samples[i] = Lowpass(samples[i], HFF, samplingRate);
  }
  return samples;
};

export const calculateConfig = ({
  pGain,
  pSpeed,
  width,
  height,
  adcGain,
  ecgData,
  samplingRate,
  pLFF,
}: {
  pGain?: any;
  pSpeed?: any;
  width: any;
  height: any;
  adcGain: any;
  ecgData: any[];
  samplingRate: any;
  pLFF?: any;
}) => {
  const adcZero = 0;
  const speed = pSpeed || 25;
  const gain = pGain || 2;
  const dataLength = ecgData[0].length;
  const recDuration = dataLength / samplingRate;
  const aspectRatioX = width / recDuration / speed;
  const aspectRatioY = aspectRatioX;
  const secondsToPix = speed * aspectRatioX;
  const indexToPix = (speed * aspectRatioX) / samplingRate;
  const majorDivSize = width / recDuration;
  const millivoltToPix = gain * aspectRatioY;
  const LFF = pLFF || 1;
  const HFF = 40;
  return {
    adcGain,
    dataLength,
    recDuration,
    aspectRatioX,
    aspectRatioY,
    secondsToPix,
    indexToPix,
    majorDivSize,
    millivoltToPix,
    LFF,
    HFF,
    adcZero,
    width,
    height,
    samplingRate,
  };
};

// Check for css when change
export const ID_DIV_STRIP_RHYTHM_EVENT = 'stripEventsRhythmEvents';
export const ID_DIV_STRIP_ARTIFACT = 'stripArtifactsBeatHr';
export const ID_DIV_STRIP_AFIB = 'stripAfibsBeatHr';
export const ID_DIV_STRIP_BEATS = 'stripBeatsBeatHr';

export const HR_MODAL_TYPE = {
  MIN_HR: 'MINHR',
  MAX_HR: 'MAXHR',
  AVG_HR: 'AVGHR',
};

export const HR_TYPE = {
  MIN_HR: 'MINHR',
  MAX_HR: 'MAXHR',
  AVG_HR: 'AVGHR',
};

/**
 * Collects the distinct calendar days covered by a set of beats. Converts each beat epoch into the study timezone, truncates it to the start of the day and de-duplicates the resulting ISO strings through a Set.
 *
 * @param beats - Beat epoch timestamps.
 * @param timezoneOffset - Study timezone offset applied before truncating, zero by default.
 * @returns The distinct day-start ISO strings covered by the beats.
 */
export const getListBeatTime = (beats: any[] = [], timezoneOffset = 0) => {
  const uniqueDate = new Set();
  beats.forEach((epoch) => {
    uniqueDate.add(convertToMomentJs(epoch, timezoneOffset)?.startOf('days')?.toISOString());
  });
  return Array.from(uniqueDate);
};

/**
 * Builds the display title of an ECG event, numbering it when the column holds several events of the same type. Resolves the type through oldType for deleted beats, maps it with convertEventNameFromType, falls back to Minute info, and appends the one-based order among same-type siblings.
 *
 * @param event - The event whose title is being built.
 * @param data - All events in the column, used to count and order same-type siblings.
 * @returns The event title, suffixed with its order when the type repeats.
 */
export const formatEventTitle = (event: any, data: any[]) => {
  const countSameType = data.filter(
    (x) => x.type === event.type || x.oldType === event.oldType,
  ).length;
  const type =
    event?.type === TYPE_BEAT_ENUM.DELETED ? event?.oldType : event?.type || event?.oldType;
  const title = type ? (convertEventNameFromType as any)[type] : 'Minute info';
  if (countSameType > 1) {
    const orderOfSameType = data
      .filter((x) => x.type === event.type || x.oldType === event.oldType)
      .findIndex((x) => x.idEvent === event.idEvent);
    return `${title} ${orderOfSameType + 1}`;
  }
  return title;
};

/**
 * Decides whether an edited ECG event still equals its original, so an unchanged event can be dropped from the pending changes. Deep-compares every key of the original, ignoring stripImgs, technicianComments and title, and treats an empty side as different.
 *
 * @param original - The event as it was before editing.
 * @param newData - The event in its current edited form.
 * @returns True when every compared field is unchanged.
 */
const compareSameEvent = (original: any, newData: any) => {
  if (_.isEmpty(original) || _.isEmpty(newData)) return false;
  let isEqual = true;
  _.forEach(Object.keys(original), (key) => {
    if (
      !_.isEqual(original[key], newData[key]) &&
      key !== 'stripImgs' &&
      key !== 'technicianComments' &&
      key !== 'title'
    ) {
      isEqual = false;
      return false;
    }
    return true;
  });
  return isEqual;
};

/**
 * Records the removal of one strip capture from an ECG event in the pending-changes list. Clones the list, drops the capture, rebuilds the flattened draw data, marks the event for image update and keeps server-side captures as delete entries; an event that again equals its origin is removed, and an event not yet pending is pushed instead.
 *
 * @param events - The pending ECG event changes.
 * @param updatedEvent - The event and the captureId or stripId being removed.
 * @param config - Options carrying the isRemove flag that enables the removal branch.
 * @returns The pending event list, de-duplicated by idEvent, after the capture removal.
 */
export const updateListEventRemovedStrip = (
  events: any[] = [],
  updatedEvent: any = {},
  config: any = {},
) => {
  const { isRemove } = config || {};
  const cloneEvents = _.cloneDeep(events);
  const event = _.find(cloneEvents, (x) => x.idEvent === updatedEvent?.idEvent);
  if (event) {
    const { originEvent } = event || {};
    if (isRemove) {
      const { captureId, stripId } = updatedEvent || {};
      const { captures } = updatedEvent || {};
      const newCapture = _.cloneDeep(captures);
      const removedCapture = _.find(
        updatedEvent?.captures,
        (x) => x.captureId === captureId || x.stripId === stripId,
      );
      _.remove(newCapture, (x: any) => x.captureId === captureId);
      _.assign(event, { captures: newCapture, draw: _.flatten(_.map(newCapture, (x) => x.draw)) });
      if (!removedCapture.isNewCapture) {
        _.assign(event, {
          isUpdateImage: true,
          removedCaptures: _.uniqBy(
            [...(event.removedCaptures || []), { ...removedCapture, action: 'delete' }],
            'captureId',
          ),
        });
      }
      const isEqualOrigin = compareSameEvent(originEvent, event);
      if (isEqualOrigin) {
        _.remove(cloneEvents, (x) => x.idEvent === updatedEvent?.idEvent);
      }
      return _.uniqBy(cloneEvents, 'idEvent');
    }
  } else if (isRemove) {
    const { captureId, stripId } = updatedEvent || {};
    const { captures } = updatedEvent || {};
    const newCapture = _.cloneDeep(captures);
    const removedCapture = _.find(updatedEvent?.captures, (x) => x.captureId === captureId);
    _.remove(newCapture, (x: any) => x.captureId === captureId || x.stripId === stripId);
    cloneEvents.push({
      ...updatedEvent.event,
      captures: newCapture,
      draw: _.flatten(_.map(newCapture, (x) => x.draw)),
      isUpdateImage: true,
      removedCaptures: !removedCapture.isNewCapture
        ? _.uniqBy(
            [...(updatedEvent.removedCaptures || []), { ...removedCapture, action: 'delete' }],
            'captureId',
          )
        : _.uniqBy([...(updatedEvent.removedCaptures || [])], 'captureId'),
    });
    return _.uniqBy(cloneEvents, 'idEvent');
  }

  return cloneEvents;
};

/**
 * Reads the event currently selected inside an HR summary column. Returns null when the column carries no events, otherwise indexes into it.
 *
 * @param selectedColumnInfo - Events belonging to the selected column.
 * @param selectedEventIndex - Position of the selected event within the column.
 * @returns The selected event, or null when the column is empty.
 */
export const getSelectedEvent = (selectedColumnInfo: any, selectedEventIndex: any) =>
  _.isEmpty(selectedColumnInfo) ? null : selectedColumnInfo[selectedEventIndex];

/**
 * Groups the events of a selected HR summary column by their display type. Reduces the list into a record keyed by the eventTypeConvert name, or Minute info when the event has no type, each bucket collecting the events and their original positions.
 *
 * @param selectedColumnInfo - Events belonging to the selected column, in display order.
 * @returns A record mapping each type name to its events and eventIndexes.
 */
export const groupSelectedColumnInfo = (selectedColumnInfo: any) => {
  let eventIndex = 0;
  return _.reduce(
    selectedColumnInfo,
    (result: Record<string, any>, x: any) => {
      const matchObj =
        result[x?.type ? (eventTypeConvert as any)[x.type || x.oldType] : 'Minute info'] ||
        (result[x?.type ? (eventTypeConvert as any)[x.type || x.oldType] : 'Minute info'] = {
          events: [],
          eventIndexes: [],
        });
      matchObj.events.push(x);
      matchObj.eventIndexes.push(eventIndex);
      eventIndex += 1;
      return result;
    },
    {} as Record<string, any>,
  );
};

/**
 * Overlays locally edited daily HR summaries on top of the ones loaded from the server. Walks the original list in order and spreads the updated entry matching the same date over each original entry.
 *
 * @param originalDailySummaries - Daily HR summaries as loaded from the server.
 * @param updatedDailySummaries - Locally edited daily HR summaries, matched by date.
 * @returns A new list of daily summaries with the edits merged in by date.
 */
export const combineDailySummaries = (
  originalDailySummaries: any[],
  updatedDailySummaries: any[],
) => {
  const dailySummariesCombine = [];
  for (let i = 0; i < originalDailySummaries.length; i += 1) {
    dailySummariesCombine.push({
      ...originalDailySummaries[i],
      ...updatedDailySummaries.find((x) => x.date === originalDailySummaries[i].date),
    });
  }
  return dailySummariesCombine;
};

export const handleResetAppliedBuldBeats = ({
  appliedBuldBeats = [] as any[],
  strips = [] as any[],
  type,
  bulkBeatsChanges,
}: {
  appliedBuldBeats?: any[];
  strips?: any[];
  type: any;
  bulkBeatsChanges: any;
}) => {
  const newAppliedBuldBeats = _.cloneDeep(appliedBuldBeats);
  _.forEach(strips, (strip) => {
    const index = _.findIndex(newAppliedBuldBeats, (x) => x.id === strip.id);
    const isInBuldCells = _.find(bulkBeatsChanges, (x) => x.cellId === strip.cellId);
    const isReset =
      (!type || strip?.originalType === type || strip?.oldType === type) && isInBuldCells;
    if (isReset) {
      newAppliedBuldBeats.push(strip);
    } else if (index > -1) {
      newAppliedBuldBeats.splice(index, 1);
    }
  });
  return _.uniqBy(newAppliedBuldBeats, 'id');
};
